import { useEffect, useRef, useState } from 'react'
import { AppProvider, Page, Card, Spinner, Banner, Text, BlockStack, InlineStack } from '@shopify/polaris'
import enTranslations from '@shopify/polaris/locales/en.json'
import { setSessionTokenGetter } from '@/api/client'
import { billingApi } from '@/api/billing'

/**
 * Lands here twice during the Shopify billing round-trip:
 *
 * 1. First landing — Shopify redirects the top-level tab back here after the
 *    merchant approves the charge. There is no `host` param yet (the tab is
 *    unembedded), so App Bridge (loaded via the CDN script in index.html)
 *    hasn't initialised. We bounce into the embedded admin, which re-issues
 *    a real `host` param.
 * 2. Second landing — now embedded with a `host`. window.shopify is available
 *    (not via the shared AppBridgeWrapper, whose no-host fallback shows an
 *    infinite loading spinner rather than performing this bounce), so we
 *    wire up the session token getter directly, then call the activation
 *    endpoint and redirect into the app.
 */
export function BillingCallbackPage() {
  const [status, setStatus] = useState<'activating' | 'success' | 'error'>('activating')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const ranOnce = useRef(false)

  useEffect(() => {
    if (ranOnce.current) return
    ranOnce.current = true

    const params   = new URLSearchParams(window.location.search)
    const host     = params.get('host')
    const shop     = params.get('shop')
    const chargeId = params.get('charge_id')
    const apiKey   = import.meta.env.VITE_SHOPIFY_API_KEY as string

    if (!host) {
      if (!shop) {
        setStatus('error')
        setErrorMessage('Missing shop parameter — please return to Shopify Admin and try subscribing again.')
        return
      }
      const qs = chargeId ? `?charge_id=${encodeURIComponent(chargeId)}` : ''
      window.top!.location.href = `https://${shop}/admin/apps/${apiKey}${qs}`
      return
    }

    setSessionTokenGetter(() => window.shopify!.idToken())

    if (!chargeId) {
      setStatus('error')
      setErrorMessage('Missing charge reference — please return to Shopify Admin and try subscribing again.')
      return
    }

    billingApi
      .activate(Number(chargeId))
      .then(() => {
        setStatus('success')
        setTimeout(() => {
          window.location.href = '/billing'
        }, 1500)
      })
      .catch((err: any) => {
        setStatus('error')
        setErrorMessage(err?.message ?? 'Activation failed. Please try again from the Billing page.')
      })
  }, [])

  return (
    <AppProvider i18n={enTranslations}>
      <Page title="Activating your subscription">
        <Card>
          <BlockStack gap="400">
            {status === 'activating' && (
              <InlineStack gap="300" blockAlign="center">
                <Spinner accessibilityLabel="Activating subscription" size="small" />
                <Text as="p">Confirming your subscription with Shopify…</Text>
              </InlineStack>
            )}
            {status === 'success' && (
              <Banner tone="success">
                <Text as="p">Subscription activated! Redirecting…</Text>
              </Banner>
            )}
            {status === 'error' && (
              <Banner tone="critical">
                <Text as="p">{errorMessage}</Text>
              </Banner>
            )}
          </BlockStack>
        </Card>
      </Page>
    </AppProvider>
  )
}
